/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { createContext, useContext, useEffect, useState } from "react";
import { detectCaptchaFromScript } from "../utils/recaptchaUtils";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import TurnstileWidget from "./TurnstileWidget";
import { prodModeLog } from "../utils/utils";

type CaptchaProvider = "grecaptcha" | "turnstile" | null;

interface CaptchaContextProps {
  provider: CaptchaProvider;
}

const CaptchaContext = createContext<CaptchaContextProps | undefined>(
  undefined
);

export const CaptchaProviderContext: React.FC<{
  children: React.ReactElement | React.ReactElement[];
}> = ({ children }) => {
  const [provider, setProvider] = useState<CaptchaProvider>(null);

  useEffect(() => {
    (async () => {
      const start = performance.now();
      const loadedProvider = await detectCaptchaFromScript(5000); // 5s to await google recaptcha readable
      const end = performance.now();
      // eslint-disable-next-line no-console
      console.log(
        `fetchWithTimeout function check google recaptcha a took ${(
          end - start
        ).toFixed(2)} ms`
      );

      setProvider(loadedProvider);
    })();
  }, []);

  prodModeLog("Captcha Context is available with provider " + provider);

  return (
    <CaptchaContext.Provider value={{ provider }}>
      {provider === "grecaptcha" && (
        <GoogleReCaptchaProvider
          reCaptchaKey={import.meta.env.VITE_APP_RECAPTCHA_ENTERPRISE_KEY}
          useEnterprise
          scriptProps={{
            /**
          If both are present, browsers typically treat it as async (priority = async).
          That means:
          Script downloads in parallel.
          Executes as soon as it’s ready, not waiting for HTML parse.
          Execution order with other scripts can’t be guaranteed.
        */
            defer: true, // Whether script should use `defer`
            async: true, // Whether script should load asynchronously
          }}
        >
          {children}
        </GoogleReCaptchaProvider>
      )}
      {provider === "turnstile" && (
        <>
          <TurnstileWidget>{children}</TurnstileWidget>
        </>
      )}
      {provider === null && children}
    </CaptchaContext.Provider>
  );
};

export function useCaptcha() {
  const context = useContext(CaptchaContext);
  if (!context) {
    throw new Error("useCaptcha must be used within CaptchaProvider");
  }
  return context;
}
